/**
 * rtDoseModule.js — Этап 4: чтение RTDOSE и изодозные линии.
 *
 * Ядро расчёта (rtDoseCore) проверено автономными тестами: масштаб дозы,
 * marching squares (площадь изолинии совпадает с π·r²), точки контура точно на
 * уровне дозы, перевод пиксель→мир на наклонённой сетке.
 *
 * Здесь — разбор DICOM RTDOSE через dcmjs (как в rtStructModule) и выдача
 * изодозных полилиний в мировых координатах, готовых к наложению на срезы.
 * Сам разбор и отрисовку на MPR проверить можно только вживую.
 */

import { log } from '../utils/logger.js';
import { registerAPIs } from '../core/viewerAPI.js';
import { cornerstone, getRenderingEngine } from '../core/viewportState.js';
import {
  doseSlice, isodoseWorldLines, percentLevels, doseGridPixelToWorld,
} from './rtDoseCore.js';
import { isodoseCanvasPolylines } from './rtIsodoseMapCore.js';
import { marchingCubes, meshToOBJ, closeHoles } from './rtIsosurfaceCore.js';
import { helpIcon } from './rtKnowledgeModule.js';   // [12.0.17] значок «?» у заголовка

let _dose = null; // { grid, cols, rows, frames, geom, units, max, forUID }

/**
 * [7.0.23] Извлечение дозы вынесено в rtDoseIOCore.doseFromNaturalized —
 * чистое ядро под тестами (разрядность 8/16/32, знак, нормировка
 * GridFrameOffsetVector, честные ошибки на сжатых/битых данных). Здесь —
 * только получение натурализованного датасета: dcmjs, а без него — наш кодек
 * (Explicit/Implicit VR LE). Раньше без dcmjs разбор умирал.
 */
export async function parseRTDose(arrayBuffer) {
  let ds = null, via = 'dcmjs';
  try {
    const mod = await import('dcmjs');
    const dcmjs = mod.default || mod;
    const DicomMessage = dcmjs.data?.DicomMessage || dcmjs.DicomMessage;
    const DicomMetaDictionary = dcmjs.data?.DicomMetaDictionary || dcmjs.DicomMetaDictionary;
    if (!DicomMessage || !DicomMetaDictionary) throw new Error('нет API dcmjs');
    const dicomData = DicomMessage.readFile(arrayBuffer);
    ds = DicomMetaDictionary.naturalizeDataset(dicomData.dict);
  } catch (e) {
    log.warn(`[RTDOSE] dcmjs не разобрал файл (${e.message}) — пробую запасной кодек`);
    via = 'запасной кодек';
    const { readPart10, naturalizeLite } = await import('./rtDicomCodecCore.js');
    const bytes = arrayBuffer instanceof Uint8Array ? arrayBuffer : new Uint8Array(arrayBuffer);
    ds = naturalizeLite(readPart10(bytes).dataset);
  }

  const { doseFromNaturalized } = await import('./rtDoseIOCore.js');
  const dose = doseFromNaturalized(ds);
  for (const w of dose.warnings) log.warn('[RTDOSE] ' + w);
  _dose = dose;
  log.info(`[RTDOSE] Загружено через ${via}: ${dose.cols}×${dose.rows}×${dose.frames}, макс ${dose.max.toFixed(2)} ${dose.units}`);
  return _dose;
}

export async function loadRTDoseFromFile(file) {
  const buf = await file.arrayBuffer();
  return parseRTDose(buf);
}

/**
 * [7.0.26] Тестовая доза-«шар» для проверки БЕЗ станции планирования:
 * генерирует настоящий файл RTDOSE (наш кодек), привязанный к загруженной
 * серии (её Frame of Reference), с центром в точке взгляда (фокус камеры
 * первого MPR-окна) или в центре тома. 20 Гр в центре → 0 на 40 мм;
 * изодоза 10 Гр — сфера ровно 20 мм: проверяется линейкой приложения.
 */
export async function makeTestRTDose(opts = {}) {
  const vols = cornerstone?.cache?.getVolumes?.() || [];
  const vol = vols[0];
  if (!vol) throw new Error('Сначала загрузите серию (КТ или МРТ) — тестовая доза привязывается к ней.');

  // Frame of Reference серии — чтобы доза легла в её систему координат
  let forUID = null;
  try {
    const id0 = (vol.imageIds || [])[0];
    forUID = id0 ? (cornerstone.metaData.get('imagePlaneModule', id0)?.frameOfReferenceUID || null) : null;
  } catch (e) { /* */ }
  if (!forUID) log.warn('[RTDOSE] FrameOfReferenceUID серии не найден — тестовая доза получит собственный.');

  // Центр: фокус камеры первого MPR-окна (куда смотрит врач), иначе центр тома
  let center = null;
  try {
    const vp = (getRenderingEngine()?.getViewports?.() || [])
      .find(v => String(v?.type || '').toLowerCase().includes('orthographic'));
    const fp = vp?.getCamera?.()?.focalPoint;
    if (fp && fp.every(Number.isFinite)) center = [fp[0], fp[1], fp[2]];
  } catch (e) { /* */ }
  if (!center) {
    try {
      const o = vol.origin, d = vol.dimensions, s = vol.spacing, D = vol.direction || [1, 0, 0, 0, 1, 0, 0, 0, 1];
      const half = [ (d[0] - 1) * s[0] / 2, (d[1] - 1) * s[1] / 2, (d[2] - 1) * s[2] / 2 ];
      center = [0, 1, 2].map(i => o[i] + D[i * 3] * half[0] + D[i * 3 + 1] * half[1] + D[i * 3 + 2] * half[2]);
    } catch (e) { /* */ }
  }
  if (!center) throw new Error('Не удалось определить центр тома.');

  const { makeTestDoseFileBytes } = await import('./rtDoseIOCore.js');
  const bytes = await makeTestDoseFileBytes({ center, forUID: forUID || undefined, ...opts });
  const filename = 'RTDOSE_TEST_sphere.dcm';
  const blob = new Blob([bytes], { type: 'application/dicom' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 2000);
  log.info(`[RTDOSE] Тестовая доза записана: центр [${center.map(v => v.toFixed(1))}], FoR ${forUID || 'сгенерирован'}, ${blob.size} байт`);
  return { filename, center, forUID, bytes: blob.size };
}

export function getDose() { return _dose; }

/** Изодозные полилинии (мировые LPS) для кадра frame и уровней Гр. */
export function isodoseLinesForFrame(frame, levelsGy) {
  if (!_dose) return [];
  const field = doseSlice(_dose.grid, _dose.cols, _dose.rows, frame);
  const out = [];
  for (const lv of levelsGy) {
    const lines = isodoseWorldLines(field, _dose.cols, _dose.rows, lv, _dose.geom, frame);
    if (lines.length) out.push({ gy: lv, lines });
  }
  return out;
}

/** Уровни по процентам от опорной (назначенной) дозы. */
export function levelsFromPrescription(refGy, percents) {
  return percentLevels(refGy, percents).map(x => x.gy);
}

// ── отрисовка изодоз на MPR (проекция мировых линий в канву) ────────────────
// [7.0.23] Вычислительная часть — в rtIsodoseMapCore (под тестами): базис
// плоскости из камеры (с защитой от viewUp ∥ normal), шаг/размер выборки,
// полилинии в канве, цвета. Здесь остались SVG и события.
let _draw = { levels: [], colors: [], on: false, handlers: [] };

function _mprViewports() {
  try {
    return (getRenderingEngine()?.getViewports?.() || [])
      .filter(vp => String(vp?.type || '').toLowerCase().includes('orthographic'));
  } catch (e) { return []; }
}

function _overlayFor(vp) {
  const el = vp.element || vp.canvas?.parentElement;
  if (!el) return null;
  let svg = el.querySelector(':scope > svg.isodose-ovl');
  if (!svg) {
    svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('class', 'isodose-ovl');
    svg.style.cssText = 'position:absolute;left:0;top:0;width:100%;height:100%;pointer-events:none;z-index:5';
    if (getComputedStyle(el).position === 'static') el.style.position = 'relative';
    el.appendChild(svg);
  }
  return svg;
}

/** Нарисовать изодозы уровней (Гр) на всех MPR-окнах для их текущих плоскостей. */
export function drawIsodoses(levelsGy, opts = {}) {
  if (!_dose) { log.warn('[RTDOSE] Сначала загрузите RTDOSE.'); return; }
  const levels = (levelsGy && levelsGy.length ? levelsGy : _draw.levels).slice().sort((a, b) => b - a);
  _draw.levels = levels; _draw.on = true;

  for (const vp of _mprViewports()) {
    try {
      const cam = vp.getCamera?.(); if (!cam) continue;
      const svg = _overlayFor(vp); if (!svg) continue;
      while (svg.firstChild) svg.removeChild(svg.firstChild);

      const layers = isodoseCanvasPolylines(_dose, cam, (w) => vp.worldToCanvas?.(w) || null, levels, opts);
      for (const layer of layers) {
        for (const line of layer.lines) {
          const pl = document.createElementNS('http://www.w3.org/2000/svg', 'polyline');
          pl.setAttribute('points', line.map(p => `${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' '));
          pl.setAttribute('fill', 'none');
          pl.setAttribute('stroke', layer.color);
          pl.setAttribute('stroke-width', String(opts.width || 1.5));
          pl.setAttribute('opacity', String(opts.opacity ?? 0.9));
          svg.appendChild(pl);
        }
      }
    } catch (e) { log.warn('[RTDOSE] отрисовка окна:', e.message); }
  }

  // перерисовка при прокрутке/повороте камеры (best-effort)
  if (!_draw.handlers.length) {
    for (const vp of _mprViewports()) {
      const el = vp.element || vp.canvas?.parentElement; if (!el) continue;
      const h = () => { if (_draw.on) drawIsodoses(_draw.levels, opts); };
      for (const ev of ['CORNERSTONE_CAMERA_MODIFIED', 'cornerstonecameramodified', 'wheel']) {
        try { el.addEventListener(ev, h); _draw.handlers.push([el, ev, h]); } catch (e) { /* */ }
      }
    }
  }
  log.info(`[RTDOSE] Изодозы нарисованы: ${levels.map(v => v.toFixed(1) + 'Гр').join(', ')}`);
}

export function clearIsodoses() {
  _draw.on = false;
  for (const [el, ev, h] of _draw.handlers) { try { el.removeEventListener(ev, h); } catch (e) { /* */ } }
  _draw.handlers = [];
  for (const vp of _mprViewports()) {
    const el = vp.element || vp.canvas?.parentElement;
    el?.querySelectorAll?.(':scope > svg.isodose-ovl').forEach(s => s.remove());
  }
  log.info('[RTDOSE] Изодозы убраны.');
}

// ── изоповерхность дозы (3D, marching cubes) ────────────────────────────────
/** Треугольная изоповерхность уровня level (Гр) в мировых координатах. */
export function buildIsodoseSurface(level) {
  if (!_dose) return null;
  const idxToWorld = (x, y, z) => doseGridPixelToWorld(_dose.geom, x, y, z);
  return marchingCubes(_dose.grid, _dose.cols, _dose.rows, _dose.frames, level, idxToWorld);
}

/** Скачать изоповерхность уровня level как OBJ (просмотр в любом 3D-редакторе). */
export function downloadIsodoseSurfaceOBJ(level) {
  const mesh = buildIsodoseSurface(level);
  if (!mesh || !mesh.count) { log.warn('[RTDOSE] Изоповерхность пуста на уровне', level); return null; }
  try {
    // Водонепроницаемый меш для 3D-печати: гарантированно закрываем дыры.
    const sealed = closeHoles(mesh.positions);
    const obj = meshToOBJ(sealed.positions, `isodose_${level}Gy`);
    const blob = new Blob([obj], { type: 'text/plain' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `isodose_${level}Gy.obj`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 2000);
    // [7.0.18] Замечание Андрея: раньше печаталось число треугольников ДО заклейки
    // (mesh.count), хотя в OBJ уходит запечатанный меш. Теперь — итоговое число.
    const finalTris = sealed.positions.length / 9;
    log.info(`[RTDOSE] Изоповерхность ${level} Гр: ${finalTris} треуг. (${mesh.count} до заклейки)`
      + (sealed.holesClosed ? `, закрыто дыр ${sealed.holesClosed} (+${sealed.trianglesAdded} треуг.)` : ', водонепроницаема') + ' → OBJ');
  } catch (e) { log.warn('[RTDOSE] OBJ:', e.message); }
  return mesh;
}

// ── UI (загрузка RTDOSE + сведения) ─────────────────────────────────────────
export function mountDoseUI() {
  if (document.getElementById('rtdose-box')) return;
  const host = document.getElementById('rtstruct-panel') || document.body;
  const box = document.createElement('div');
  box.id = 'rtdose-box';
  box.className = 'rt-sec';
  box.innerHTML = `
    <details class="rt-fold" open>
      <summary class="rt-sec-head"><span class="rt-sec-title">Доза${helpIcon('isodose')}</span></summary>
    <button id="dose-load" class="rt-btn rt-btn--primary">Загрузить RTDOSE</button>
    <input id="dose-file" type="file" accept=".dcm,application/dicom" style="display:none">
    <button id="dose-test" class="rt-btn rt-btn--sm"
      title="Скачать синтетический RTDOSE-шар (20 Гр в центре, 0 на 40 мм), привязанный к загруженной серии — для проверки без станции планирования">Тестовая доза (шар 20 Гр)</button>
    <div id="dose-info" class="rt-out"></div>
    <div id="dose-ctrls" style="display:none">
      <label class="rt-field"><span>Назначенная доза, Гр</span>
        <input id="dose-rx" class="rt-input" type="number" value="" step="0.1"></label>
      <label class="rt-field"><span>Уровни (% от назначенной или Гр, через запятую)</span>
        <input id="dose-levels" class="rt-input" type="text" value="95,90,80,50,30"></label>
      <div class="rt-row">
        <button id="dose-show" class="rt-btn rt-btn--sm">Показать изодозы</button>
        <button id="dose-hide" class="rt-btn rt-btn--sm">Убрать</button>
      </div>
      <button id="dose-surf" class="rt-btn rt-btn--sm">Изоповерхность верхнего уровня → OBJ</button>
    </div>
    </details>`;
  host.appendChild(box);
  const info = box.querySelector('#dose-info'), ctrls = box.querySelector('#dose-ctrls');
  const fi = box.querySelector('#dose-file');
  box.querySelector('#dose-load').onclick = () => fi.click();
  box.querySelector('#dose-test').onclick = async () => {
    info.textContent = '⏳ Готовлю тестовую дозу…';
    try {
      const r = await makeTestRTDose();
      info.innerHTML = `🧪 Скачан <b>${r.filename}</b> (центр [${r.center.map(v => v.toFixed(0))}] мм).<br>`
        + 'Теперь загрузите его кнопкой «☢ Загрузить RTDOSE» — изодозы лягут на вашу серию.';
    } catch (err) { info.textContent = '❌ ' + err.message; log.error('[RTDOSE] тестовая доза:', err); }
  };
  fi.onchange = async (e) => {
    const f = e.target.files && e.target.files[0]; if (!f) return;
    info.textContent = '⏳ Читаю RTDOSE…';
    try {
      const d = await loadRTDoseFromFile(f);
      info.innerHTML = `сетка ${d.cols}×${d.rows}×${d.frames}<br>макс доза <b>${d.max.toFixed(2)} ${d.units}</b>`;
      box.querySelector('#dose-rx').value = d.max.toFixed(1);
      ctrls.style.display = 'block';
    } catch (err) { info.textContent = '❌ ' + err.message; log.error('[RTDOSE]', err); }
    finally { fi.value = ''; }
  };
  box.querySelector('#dose-show').onclick = () => {
    const rx = parseFloat(box.querySelector('#dose-rx').value) || (_dose ? _dose.max : 0);
    const raw = box.querySelector('#dose-levels').value.split(',').map(s => s.trim()).filter(Boolean);
    // «50» — процент от назначенной; «12.5гр»/«12.5» с точкой и >... трактуем: с % если <=100 и целое? Надёжнее:
    const levels = raw.map(s => {
      const num = parseFloat(s.replace(',', '.'));
      if (/gy|гр/i.test(s)) return num;               // явно Гр
      return num <= 100 ? rx * num / 100 : num;        // ≤100 — процент, иначе Гр
    }).filter(v => v > 0);
    drawIsodoses(levels);
  };
  box.querySelector('#dose-hide').onclick = () => clearIsodoses();
  box.querySelector('#dose-surf').onclick = () => {
    const rx = parseFloat(box.querySelector('#dose-rx').value) || (_dose ? _dose.max : 0);
    const raw = box.querySelector('#dose-levels').value.split(',').map(s => s.trim()).filter(Boolean);
    const levels = raw.map(s => {
      const num = parseFloat(s.replace(',', '.'));
      if (/gy|гр/i.test(s)) return num;
      return num <= 100 ? rx * num / 100 : num;
    }).filter(v => v > 0);
    if (!levels.length) { log.warn('[RTDOSE] Уровни не заданы.'); return; }
    downloadIsodoseSurfaceOBJ(Math.max(...levels));   // верхний (самый жёсткий) уровень
  };
}

export function initRTDose() {
  try {
    mountDoseUI();
    registerAPIs({
      loadRTDose: loadRTDoseFromFile, parseRTDose, getDose,
      isodoseLinesForFrame, levelsFromPrescription, drawIsodoses, clearIsodoses,
      buildIsodoseSurface, downloadIsodoseSurfaceOBJ, makeTestRTDose,
    });
    log.info('[RTDOSE] Модуль дозы инициализирован — 1.2 (Этап 4: изодозы + изоповерхность)');
  } catch (e) { log.error('[RTDOSE] init failed:', e); }
}

export default { initRTDose, parseRTDose, loadRTDoseFromFile, getDose, isodoseLinesForFrame, levelsFromPrescription, drawIsodoses, clearIsodoses, buildIsodoseSurface, downloadIsodoseSurfaceOBJ };
